import {
  EMBED_PRESETS,
  type EmbedGround,
  type EmbedLook,
  embedFrame,
  embedScript,
  embedShortcode,
  originsOf,
} from "@countdown/theme";
import { type ReactElement, useId, useState } from "react";

import { type DesignElement, part, useSentRequest } from "@/components/CalendarDesign";
import { FormField } from "@/components/FormField";
import { HelpTip } from "@/components/HelpTip";
import { Notice } from "@/components/Notice";
import { SaveForm } from "@/components/SaveForm";
import { Advanced } from "@/components/Section";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { type ActionDetail, saveActionSection } from "@/lib/api";
import { sameValue } from "@/lib/calendar-draft";
import { HELP_TIPS } from "@/lib/help";

/**
 * Where the calendar of an action lives, and which websites may show it in a frame (D36).
 *
 * The address is the action's own path under the tenant's calendar domain: several calendars
 * run side by side, and this is the link that goes on print and into the newsletter. A website
 * listed below may frame it; for it the section hands out the code (D41, `embed.ts` of the theme): the
 * shortcode for the magazines' WordPress, frame and script line for a website that keeps a
 * script, the bare frame for any website, and the frame with each door in a new tab. How the
 * calendar looks in the frame is set in the design tab (element "Auf Websites", D49).
 */
export function EmbedSettings({
  data,
  participantBaseUrl,
  readOnly,
  onSaved,
}: {
  readonly data: ActionDetail;
  readonly participantBaseUrl: string | null;
  readonly readOnly: boolean;
  readonly onSaved: () => void;
}): ReactElement {
  const calendarUrl =
    participantBaseUrl === null ? null : `${participantBaseUrl}/${data.stable_link_slug}/`;
  const origins = data.embed_origins ?? [];

  return (
    <>
      {calendarUrl === null ? (
        <Notice tone="info">Für diesen Mandanten ist keine Teilnehmer-Domain eingetragen.</Notice>
      ) : (
        <CopyField id="kalender-adresse" label="Adresse des Kalenders" value={calendarUrl} />
      )}

      {/* Open while empty: without an allowed website no code below works. */}
      <Advanced
        summary={`Erlaubte Websites (${String(origins.length)})`}
        open={origins.length === 0}
      >
        <SaveForm
          label="Erlaubte Websites"
          readOnly={readOnly}
          onSaved={onSaved}
          submit={(form) =>
            saveActionSection(
              "embed-origins",
              { origins: originsOf(String(form.get("embed_origins") ?? "")) },
              data.action_id,
            )
          }
        >
          <div className="grid gap-1.5">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="embed_origins">Websites, die den Kalender einbetten dürfen</Label>
              <HelpTip topic="erlaubten Websites">{HELP_TIPS.embedOrigins}</HelpTip>
            </div>
            <Textarea
              id="embed_origins"
              name="embed_origins"
              rows={3}
              defaultValue={origins.join("\n")}
            />
          </div>
        </SaveForm>
      </Advanced>

      {calendarUrl !== null && origins.length > 0 ? (
        <div className="mt-6 grid gap-6">
          <CopyField
            id="einbettung-shortcode"
            label="WordPress: Shortcode für den Artikel"
            hint="Im Editor als eigener Absatz einfügen. Die Website braucht dafür einmal das Plugin des Kalenders. Der Rahmen wächst mit dem Kalender, Türchen öffnen auf der Seite."
            value={embedShortcode({ calendarUrl, title: data.title })}
          />
          <Advanced summary="Weitere Codes" className="mt-0">
            <div className="grid gap-6">
              <CopyField
                id="einbettung-gast"
                label="Website mit eigenem Code: Rahmen und Skript"
                hint="Für Websites, die ein Skript im Seitencode erlauben. Der Rahmen wächst mit dem Kalender, Türchen öffnen auf der Seite."
                value={`${embedFrame({ calendarUrl, title: data.title, mode: "gast" })}\n${embedScript(calendarUrl)}`}
                multiline
              />
              <CopyField
                id="einbettung-rahmen"
                label="Einfach: nur der Rahmen"
                hint="Geht auf jeder Website, auch im Editor ohne Skript. Der Rahmen bleibt 1.000 px hoch: unter den Türchen bleibt Platz frei, ein langes Türchen scrollt im Rahmen."
                value={embedFrame({ calendarUrl, title: data.title })}
                multiline
              />
              <CopyField
                id="einbettung-tab"
                label="Nur die Türchen im Rahmen, jedes Türchen in neuem Tab"
                value={embedFrame({ calendarUrl, title: data.title, mode: "tab" })}
                multiline
              />
            </div>
          </Advanced>
        </div>
      ) : null}
    </>
  );
}

const PARTS: readonly (readonly [keyof Omit<EmbedLook, "ground">, string])[] = [
  ["header", "Kopfzeile"],
  ["title", "Überschrift"],
  ["footer", "Fußzeile"],
  ["decor", "Verzierungen"],
];

const GROUNDS: readonly (readonly [EmbedGround, string])[] = [
  ["scene", "Saisonwelt"],
  ["light", "Hell"],
  ["none", "Ohne"],
];

/**
 * How the calendar looks in the frame on a website (D39), the element "Auf Websites" of the
 * design tab (D49): which parts of the page come along and what stands behind the doors. Saved
 * with the action, so a website shows a change without new code. The four designs only set the
 * switches. The page holds the draft, so the preview draws the unsaved choice.
 */
export function EmbedLookForm({
  data,
  look,
  onChange,
  chosen,
  readOnly,
  onSaved,
}: {
  readonly data: ActionDetail;
  readonly look: EmbedLook;
  readonly onChange: (next: EmbedLook) => void;
  readonly chosen: DesignElement;
  readonly readOnly: boolean;
  readonly onSaved: () => void;
}): ReactElement {
  const id = useId();
  const stored = data.calendar_style.embed;
  const saving = useSentRequest({ look }, { look: stored }, (body) =>
    saveActionSection("embed-look", body, data.action_id),
  );

  return (
    <SaveForm
      label="Auf Websites"
      readOnly={readOnly}
      onSaved={onSaved}
      dirty={saving.dirty}
      submit={saving.submit}
      hidden={chosen !== "website"}
    >
      <div {...part(chosen, "website")}>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Entwürfe">
          {EMBED_PRESETS.map(([name, hint, preset]) => (
            <Button
              key={name}
              type="button"
              size="sm"
              variant={sameValue(look, preset) ? "default" : "outline"}
              aria-pressed={sameValue(look, preset)}
              title={hint}
              onClick={() => {
                onChange(preset);
              }}
            >
              {name}
            </Button>
          ))}
        </div>
        {/* The four designs above set these switches; most never need them one by one. */}
        <Advanced summary="Eigene Auswahl">
          {PARTS.map(([key, label]) => (
            <div className="mt-3 flex items-center gap-2" key={key}>
              <input
                id={`${id}-${key}`}
                type="checkbox"
                className="border border-input"
                checked={look[key]}
                onChange={(event) => {
                  onChange({ ...look, [key]: event.target.checked });
                }}
              />
              <Label htmlFor={`${id}-${key}`}>{label}</Label>
            </div>
          ))}
          <fieldset className="mt-4">
            <legend className="text-sm font-medium">Hintergrund</legend>
            {GROUNDS.map(([ground, label]) => (
              <div className="mt-2 flex items-center gap-2" key={ground}>
                <input
                  className="border border-input"
                  id={`${id}-${ground}`}
                  name="embed_ground"
                  type="radio"
                  checked={look.ground === ground}
                  onChange={() => {
                    onChange({ ...look, ground });
                  }}
                />
                <label className="text-sm" htmlFor={`${id}-${ground}`}>
                  {label}
                </label>
              </div>
            ))}
          </fieldset>
        </Advanced>
      </div>
    </SaveForm>
  );
}

/** A value to copy: a field or a code block, its button and what the copy did. */
export function CopyField({
  id,
  label,
  value,
  multiline = false,
  hint,
}: {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly multiline?: boolean;
  readonly hint?: string;
}): ReactElement {
  const [copied, setCopied] = useState<string | null>(null);
  const describedBy = hint === undefined ? undefined : `${id}-hint`;
  return (
    <div className="grid max-w-2xl gap-1.5">
      {multiline ? (
        <>
          <Label htmlFor={id}>{label}</Label>
          <Textarea
            id={id}
            className="bg-background font-mono text-xs"
            readOnly
            rows={6}
            value={value}
            aria-describedby={describedBy}
          />
        </>
      ) : (
        <FormField
          label={label}
          name={id}
          defaultValue={value}
          readOnly
          aria-describedby={describedBy}
        />
      )}
      {hint === undefined ? null : (
        <p className="text-sm text-muted-foreground" id={describedBy}>
          {hint}
        </p>
      )}
      <div className="mt-1 flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            navigator.clipboard
              .writeText(value)
              .then(() => {
                setCopied("Kopiert.");
              })
              .catch((error: unknown) => {
                console.error("[EmbedSettings] copy failed", error);
                setCopied("Kopieren nicht möglich. Bitte markieren und kopieren.");
              });
          }}
        >
          Kopieren
        </Button>
        {copied === null ? null : (
          <p className="text-sm" role="status">
            {copied}
          </p>
        )}
      </div>
    </div>
  );
}
